import React, { useEffect, Suspense, lazy } from "react";
import {
  BrowserRouter,
  Route,
  Routes,
  useLocation,
  Navigate,
} from "react-router-dom";
import AppContainer from "../Appcontainer";
import store from "../../store";
import { Provider } from "react-redux";
import { isAuthenticated } from "../../utils/auth";

import Accordions from "../../views/pages/Ui_Interface/Components/Accordions";
import Alerts from "../../views/pages/Ui_Interface/Components/Alerts";
import Breadcrumbs from "../../views/pages/Ui_Interface/Components/Breadcrumbs";
import Avatar from "../../views/pages/Ui_Interface/Components/Avatar";
import Badges from "../../views/pages/Ui_Interface/Components/Badges";
import ButtonCard from "../../views/pages/Ui_Interface/Components/ButtonCard";
import ButtonGroup from "../../views/pages/Ui_Interface/Components/ButtonGroup";
import Cards from "../../views/pages/Ui_Interface/Components/Cards";
import Dropdowns from "../../views/pages/Ui_Interface/Components/Dropdowns";
import Grid from "../../views/pages/Ui_Interface/Components/Grid";
import Images from "../../views/pages/Ui_Interface/Components/Images";
import Media from "../../views/pages/Ui_Interface/Components/Media";
import Modals from "../../views/pages/Ui_Interface/Components/Modals";
import Offcanvas from "../../views/pages/Ui_Interface/Components/Offcanvas";
import Pagination from "../../views/pages/Ui_Interface/Components/Pagination";
import Popover from "../../views/pages/Ui_Interface/Components/Popover";
import Progress from "../../views/pages/Ui_Interface/Components/Progress";
import Placeholder from "../../views/pages/Ui_Interface/Components/Placeholder";
import RangeSlider from "../../views/pages/Ui_Interface/Components/RangeSlider";
import Spinners from "../../views/pages/Ui_Interface/Components/Spinners";
import SweetAlert from "../../views/pages/Ui_Interface/Components/SweetAlert";
import Tabs from "../../views/pages/Ui_Interface/Components/Tabs";
import Toats from "../../views/pages/Ui_Interface/Components/Toats";
// import Tooltip from "../../views/pages/Ui_Interface/Components/Tooltip";
import Typography from "../../views/pages/Ui_Interface/Components/Typography";
import Videos from "../../views/pages/Ui_Interface/Components/Videos";
import Lightbox from "../../views/pages/Ui_Interface/Components/Lightbox";
import Carousel from "../../views/pages/Ui_Interface/Components/Carousel";
import Borders from "../../views/pages/Ui_Interface/Components/Borders";
import Breadcrumb from "../../views/pages/Ui_Interface/Components/Breadcrumb";
import Colors from "../../views/pages/Ui_Interface/Components/colors";
import UiModals from "../../views/pages/Ui_Interface/Components/uimodals";
import Spinner from "../../views/pages/Ui_Interface/Components/Spinner";
import Tooltips from "../../views/pages/Ui_Interface/Components/Tooltip";

// Loading spinner for lazy loaded components
const LoadingSpinner = () => (
  <div className="content container-fluid">
    <div className="row">
      <div
        className="col-12 d-flex justify-content-center align-items-center"
        style={{ minHeight: "60vh" }}
      >
        <div
          className="spinner-border text-primary"
          role="status"
          style={{ width: "3rem", height: "3rem" }}
        >
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    </div>
  </div>
);

// Lazy load authentication pages
const Login = lazy(() => import("../../views/pages/Authentication/Login"));
const Register = lazy(() =>
  import("../../views/pages/Authentication/Register")
);
const ForgotPassword = lazy(() =>
  import("../../views/pages/Authentication/ForgotPassword")
);
const Otp = lazy(() => import("../../views/pages/Authentication/Otp"));
const ChangePassword = lazy(() =>
  import("../../views/pages/Authentication/ChangePassword")
);
const LockScreen = lazy(() =>
  import("../../views/pages/Authentication/LockScreen")
);
const JobList = lazy(() => import("../../views/pages/Authentication/JobList"));
const JobView = lazy(() => import("../../views/pages/Authentication/JobView"));

// Lazy load error and utility pages
const Error404 = lazy(() => import("../../views/pages/Error/Error404"));
const Error500 = lazy(() => import("../../views/pages/Error/Error500"));
const ComingSoon = lazy(() => import("../../views/pages/Pages/ComingSoon"));
const UnderMaintenance = lazy(() =>
  import("../../views/pages/Pages/UnderManitenance")
);

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Protected Route component
const ProtectedRoute = ({ children }) => {
  if (!isAuthenticated()) {
    return <Navigate to="/" replace />;
  }
  return children;
};

// Public Route component (accessible only when not authenticated)
const PublicRoute = ({ children }) => {
  if (isAuthenticated()) {
    return <Navigate to="/admin-dashboard" replace />;
  }
  return children;
};

const AppRouter = () => {
  return (
    <div>
      <Provider store={store}>
        <BrowserRouter basename="/">
          <ScrollToTop />
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              {/* Public Routes */}
              <Route
                path="/"
                element={
                  <PublicRoute>
                    <Login />
                  </PublicRoute>
                }
              />
              <Route
                path="/register"
                element={
                  <PublicRoute>
                    <Register />
                  </PublicRoute>
                }
              />
              <Route
                path="/forgot-password"
                element={
                  <PublicRoute>
                    <ForgotPassword />
                  </PublicRoute>
                }
              />
              <Route
                path="/otp"
                element={
                  <PublicRoute>
                    <Otp />
                  </PublicRoute>
                }
              />
              <Route
                path="/change-password"
                element={
                  <PublicRoute>
                    <ChangePassword />
                  </PublicRoute>
                }
              />
              <Route
                path="/lock-screen"
                element={
                  <PublicRoute>
                    <LockScreen />
                  </PublicRoute>
                }
              />

              {/* Protected Routes */}
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <AppContainer />
                  </ProtectedRoute>
                }
              />

              {/* Error Pages */}
              <Route path="/error-404" element={<Error404 />} />
              <Route path="/error-500" element={<Error500 />} />
              <Route path="/coming-soon" element={<ComingSoon />} />
              <Route path="/under-maintenance" element={<UnderMaintenance />} />

              {/* Catch all route */}
              <Route path="*" element={<Navigate to="/error-404" replace />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </Provider>
    </div>
  );
};

export default AppRouter;
